import { DataMode } from '../types/census';
import { githubSnapshot, GITHUB_SNAPSHOT_AS_OF } from '../data/githubSnapshot';
import { todayIso } from './format';

export interface Repo {
  fullName: string;
  url: string;
  description: string;
  language: string | null;
  stars: number;
  created: string;
  topics: string[];
}

export interface RepoSearchResult {
  mode: DataMode;
  asOf: string;
  total: number;
  repos: Repo[];
  query: string;
  apiUrl: string;
}

export interface RepoStars {
  stars: number | null;
  mode: DataMode;
}

interface RawRepo {
  full_name: string;
  html_url: string;
  description: string | null;
  language: string | null;
  stargazers_count: number;
  created_at: string;
  topics?: string[];
}

const AI_TERMS = /\b(ai|llm|llms|agent|agents|agentic|mcp|gpt|claude|gemini|rag|openai|anthropic|transformer|diffusion|copilot)\b/i;

export function isAiRepo(r: Repo): boolean {
  return r.topics.some((t) => AI_TERMS.test(t.replace(/-/g, ' '))) || AI_TERMS.test(r.description) || AI_TERMS.test(r.fullName.replace(/[-_/]/g, ' '));
}

export function sevenDaysAgo(): string {
  return new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
}

function mapRepo(r: RawRepo): Repo {
  return {
    fullName: r.full_name,
    url: r.html_url,
    description: r.description ?? '',
    language: r.language,
    stars: r.stargazers_count,
    created: r.created_at.slice(0, 10),
    topics: r.topics ?? [],
  };
}

let searchCache: Promise<RepoSearchResult> | null = null;

export function getNewRepos(): Promise<RepoSearchResult> {
  if (searchCache) return searchCache;
  const since = sevenDaysAgo();
  const query = `created:>${since}`;
  const apiUrl = `https://api.github.com/search/repositories?q=${encodeURIComponent(query)}&sort=stars&order=desc&per_page=100`;
  searchCache = (async () => {
    try {
      const res = await fetch(apiUrl, { headers: { Accept: 'application/vnd.github+json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { total_count: number; items: RawRepo[] };
      if (!Array.isArray(json.items) || json.items.length === 0) throw new Error('Empty');
      return { mode: 'live' as const, asOf: `fetched ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC`, total: json.total_count, repos: json.items.map(mapRepo), query, apiUrl };
    } catch {
      searchCache = null; // allow retry later
      return { mode: 'snapshot' as const, asOf: GITHUB_SNAPSHOT_AS_OF, total: githubSnapshot.length, repos: githubSnapshot, query, apiUrl };
    }
  })();
  return searchCache;
}

const starCache = new Map<string, Promise<RepoStars>>();

export function getRepoStars(fullName: string, fallback: number | null): Promise<RepoStars> {
  if (!starCache.has(fullName)) {
    starCache.set(
      fullName,
      (async () => {
        try {
          const res = await fetch(`https://api.github.com/repos/${fullName}`, { headers: { Accept: 'application/vnd.github+json' } });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = (await res.json()) as { stargazers_count?: number };
          if (typeof json.stargazers_count !== 'number') throw new Error('Missing');
          return { stars: json.stargazers_count, mode: 'live' as const };
        } catch {
          return { stars: fallback, mode: 'snapshot' as const };
        }
      })(),
    );
  }
  return starCache.get(fullName) as Promise<RepoStars>;
}

export const GITHUB_FETCHED_DAY = todayIso();
